import DraftMediaFileStore from "./DraftMediaFileStore";
import mediaUtility from "../utility/MediaUtility";
import { MediaContentType, MediaKind } from "../../interfaces/MediaObject";
import { ProductDraftMedia } from "../../interfaces/ProductObject";
import { RepresentativeImageResult } from "../../interfaces/RepresentativeImageObject";

/** Displayable URL for a draft item (public URL or local object URL). */
export function previewUrlOf(item: ProductDraftMedia): string {
  return item.status === "existing" ? item.record.publicUrl : item.previewUrl;
}

export function mediaTypeOf(item: ProductDraftMedia): MediaContentType {
  return item.status === "existing" ? item.record.mediaType : item.mediaType;
}

export function kindOf(item: ProductDraftMedia): MediaKind {
  return mediaUtility.kindFromContentType(mediaTypeOf(item));
}

/** Number of IMAGE items (min_images validation counts photos only). */
export function imageCountOf(media: ProductDraftMedia[]): number {
  return media.filter((m) => kindOf(m) === "image").length;
}

/**
 * Wraps a picked File into a "new" draft item (file → store, preview URL).
 * Starts in "uploading" — the caller kicks startDraftMediaUpload immediately
 * after adding the item to the draft.
 */
export function newDraftMediaItem(
  file: File,
  mediaType: MediaContentType
): Extract<ProductDraftMedia, { status: "new" }> {
  return {
    status: "new",
    id: DraftMediaFileStore.put(file),
    mediaType,
    previewUrl: URL.createObjectURL(file),
    fileName: file.name,
    upload: { state: "uploading" },
  };
}

/**
 * Wraps a representative-catalog search result (see RepresentativeImageApiService)
 * into an "existing" draft item — it already has a stable, publicly-reachable
 * URL and nothing to upload, exactly like a previously-persisted product photo.
 * `objectPath` is deliberately set to the FULL image URL rather than a
 * bucket-relative path: we don't know (and don't need to know) which storage
 * bucket the catalog lives in, and mediaConvertor's publicUrlFromObjectPath
 * passes absolute URLs through untouched for this exact reason. This is also
 * what makes isRepresentativeMedia() below able to recognize these items
 * after a save/reload round-trip, with no separate "source" field required.
 */
export function newRepresentativeDraftMediaItem(
  image: RepresentativeImageResult
): Extract<ProductDraftMedia, { status: "existing" }> {
  return {
    status: "existing",
    record: {
      mediaType: mediaUtility.contentTypeFromName(image.imageUrl) ?? "image/jpeg",
      objectPath: image.imageUrl,
      publicUrl: image.imageUrl,
    },
  };
}

/**
 * True for an "existing" item that came from the representative-image catalog
 * rather than being uploaded by the seller (own product photo, or a
 * previously-saved one). Relies on the objectPath-is-a-full-URL convention set
 * by newRepresentativeDraftMediaItem above — own uploads always get a
 * bucket-relative objectPath (e.g. "products/{id}/product-image1.jpeg"), so
 * this is a stable, deterministic check rather than a guess at folder naming.
 * Used to badge these tiles in the uploader and to let the "no own photos"
 * toggle clean up only the picks it added.
 */
export function isRepresentativeMedia(
  item: ProductDraftMedia
): item is Extract<ProductDraftMedia, { status: "existing" }> {
  return item.status === "existing" && mediaUtility.isAbsoluteUrl(item.record.objectPath);
}

/** True while any new item is still uploading or has failed — gates Next/save. */
export function unsettledUploadsOf(media: ProductDraftMedia[]): {
  uploading: number;
  failed: number;
} {
  let uploading = 0;
  let failed = 0;
  media.forEach((item) => {
    if (item.status !== "new") return;
    if (item.upload.state === "uploading") uploading++;
    else if (item.upload.state === "error") failed++;
  });
  return { uploading, failed };
}

/** Frees a draft item's local resources (object URL + stored File). */
export function releaseDraftMediaItem(item: ProductDraftMedia): void {
  if (item.status === "new") {
    URL.revokeObjectURL(item.previewUrl);
    DraftMediaFileStore.remove(item.id);
  }
}

export function releaseAllDraftMedia(media: ProductDraftMedia[]): void {
  media.forEach(releaseDraftMediaItem);
  DraftMediaFileStore.clear();
}

/**
 * Builds the image-only FormData the AI endpoints expect ("files" entries),
 * on demand — only when the user actually triggers AI. New images come from
 * the file store; existing ones are fetched from their public URLs (a fetch
 * failure skips that image rather than aborting). Returns null when no image
 * bytes could be gathered — callers show "add a photo first".
 */
export async function buildImageFormDataFromDraft(
  media: ProductDraftMedia[]
): Promise<FormData | null> {
  const images = media.filter((item) => kindOf(item) === "image");
  if (images.length === 0) return null;

  const formData = new FormData();
  let appended = 0;

  for (const item of images) {
    let blob: Blob | null = null;
    if (item.status === "new") {
      blob = DraftMediaFileStore.get(item.id) ?? null;
    } else {
      try {
        // Raw fetch is intentional: publicUrl is the public storage CDN
        // (Supabase), NOT the backend API — httpRequest's apiDomain/bearer
        // plumbing doesn't apply (same rationale as getPublicAsset).
        const response = await fetch(item.record.publicUrl);
        if (response.ok) blob = await response.blob();
      } catch {
        /* unreachable image — skip it */
      }
    }
    if (blob) {
      appended++;
      formData.append(
        "files",
        new File([blob], `product-image${appended}.jpeg`, {
          type: blob.type || "image/jpeg",
        })
      );
    }
  }

  return appended > 0 ? formData : null;
}
